;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Configure,
    InstantSearch,
    useHits,
    useSearchBox,
} from 'react-instantsearch';

import { algoliasearch } from 'algoliasearch';
import cx from 'classnames';
import { AnimatePresence, m } from 'framer-motion';
import PropTypes from 'prop-types';

import SvgIcon from 'components/ui/SvgIcon';

import useWait from 'hooks/useWait';
import { scrollToOffset } from 'utils';

import Breadcrumbs from './Breadcrumbs';
import Card from './Card';
import Search from './Search';

import styles from './ProductsNav.module.scss';

const searchClient = algoliasearch(
    process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
    process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY
);

const steps = {
    Cheese: {
        filter: 'cut',
    },
    Butter: {
        filter: false,
    },
    'Ice Cream': {
        filter: 'type',
    },
    Yogurt: {
        filter: 'style',
    },
    'Farmstyle Cream Cheese Spreads': {
        filter: false,
    },
    'Sour Cream': {
        filter: false,
    },
    'Frozen Meals': {
        filter: false,
    },
};

const StaggerGrid = ({ children, ...rest }) => {
    return (
        <m.div
            role="radiogroup"
            aria-label="Products"
            initial="hidden"
            animate="visible"
            className={cx(styles.products, styles.grid)}
            {...rest}
        >
            {children}
        </m.div>
    );
};

StaggerGrid.propTypes = {
    children: PropTypes.any,
};

const ProductsNav = ({
    productCategories,
    onUpdate,
    additionalCategory,
    tabIndex,
    products,
}) => {
    const rootRef = useRef(null);

    const wait = useWait();

    // All products

    const [showSearch, setShowSearch] = useState(false);
    const [finderState, setFinderState] = useState({});
    const [activeProduct, setActiveProduct] = useState(null);
    const [productSize, setProductSize] = useState(null);

    /*  Handle the "drill-down" state to render the product list (search bypasses this)
    finderState = {
        category: [string | null],
        filter: [string | null],
    }
    */
    const productStep = (() => {
        if (!finderState.category) {
            return 0;
        }
        if (finderState?.category && !finderState?.filter) {
            return 1;
        }
        if (finderState?.category && finderState?.filter) {
            return 2;
        }
        return 0;
    })();

    const { query, refine, clear, instantSearchInstance } = useSearchBox({});

    const categoryConfig =
        steps?.[finderState?.category?.title] ||
        steps?.[activeProduct?.category?.title];

    const renderView = () => {
        if (activeProduct) {
            return renderSizes3();
        }
        if (query.length > 0 && !activeProduct) {
            return renderSearch();
        }
        if (
            !activeProduct &&
            ((categoryConfig?.filter && finderState?.filter) ||
                (!categoryConfig?.filter && finderState?.category))
        ) {
            return renderProducts();
        }
        if (categoryConfig?.filter) {
            return renderCategoryFilters();
        }
        return renderCategories();
    };

    const productsIndexMap = useMemo(
        () =>
            products?.reduce((a, o, i) => {
                a[o.sys.id] = i;
                return a;
            }, {}) || {},
        [products]
    );

    const transformItems = useCallback(
        items => {
            return items.map(item => {
                const index = productsIndexMap[item.objectID];
                return (
                    { objectID: item.objectID, ...products?.[index] } || item
                );
            });
        },
        [products, productsIndexMap]
    );

    const { hits } = useHits({ transformItems });

    const filterByCategory = (list, filter) => {
        return list.filter(item => item?.category?.title === filter);
    };

    useEffect(() => {
        if (activeProduct && query.length > 0) {
            setActiveProduct(null);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    const handleResetView = e => {
        e.preventDefault();
        scrollToOffset(rootRef?.current, -200, 'smooth');
        clear();
    };

    const handleCategoryClick = (e, item) => {
        handleResetView(e);
        setFinderState({ category: item });
    };

    const handleTypeClick = (e, item) => {
        handleResetView(e);
        setFinderState({ ...finderState, filter: item });
    };

    const handleProductClick = (e, item) => {
        handleResetView(e);
        setActiveProduct(item);
    };

    const filterByType = (list, filter, attr) => {
        return list.filter(item =>
            [...(item?.attributes?.[attr] || [])]
                ?.map(item => parseInt(item, 10))
                .includes(parseInt(filter, 10))
        );
    };

    const setOnlySize = product => {
        clear();

        const parseSize = product?.sizes3Collection.items[0];

        setProductSize({
            product,
            size: parseSize,
            sku: parseSize?.sku2,
            packagingCodeExample: parseSize?.packagingCodeExample,
        });
    };

    const packagingCodeExample =
        productSize?.packagingCodeExample && productSize?.packagingCodeExample;

    useEffect(() => {
        if (!productSize) {
            return;
        }

        const handleUpdateForm = packagingCodeExample
            ? {
                  size: productSize?.size,
                  sku: productSize?.sku,
                  product: productSize?.product,
                  packagingCodeExample: packagingCodeExample,
              }
            : {
                  size: productSize?.size,
                  product: productSize?.product,
                  sku: productSize?.sku,
              };

        wait(10).then(() => {
            scrollToOffset(rootRef?.current, -300, 'smooth');
        });

        productSize !== null && onUpdate(handleUpdateForm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productSize]);

    const filterProducts = products => {
        const categoryProducts = filterByCategory(
            products,
            finderState?.category?.title
        );

        if (productStep === 1 && products) {
            return categoryProducts;
        }
        if (productStep === 2 && products) {
            return filterByType(
                categoryProducts,
                finderState?.filter?.id,
                categoryConfig.filter
            );
        }

        return [];
    };

    const renderStepLabel = () => {
        if (activeProduct) {
            return 'Back';
        }
        if (query.length > 0) {
            return 'Clear';
        }
        if (productStep === 1) {
            return 'Products';
        }
        if (productStep === 2) {
            return finderState?.category?.title;
        }
        if (productStep === 3) {
            return finderState?.filter?.title;
        }
    };

    const renderCategories = () => (
        <StaggerGrid key="categories">
            {productCategories.map((item, i) => {
                return (
                    <Card
                        className={styles.card}
                        key={`cat-${i}`}
                        image={item?.thumbnail}
                        onClick={e => handleCategoryClick(e, item)}
                        tabIndex={tabIndex}
                        index={i}
                    >
                        {item.title}
                    </Card>
                );
            })}
            {additionalCategory && additionalCategory()}
        </StaggerGrid>
    );

    const renderCategoryFilters = () => {
        const categoryFilters =
            finderState?.category?.attributes?.[categoryConfig.filter]?.options;

        return (
            <StaggerGrid key="filters">
                {categoryFilters?.map((item, i) => {
                    return (
                        !item.hideContact && (
                            <Card
                                className={styles.card}
                                key={`filter-${i}`}
                                image={{ url: item?.thumb }}
                                onClick={e => handleTypeClick(e, item)}
                                tabIndex={tabIndex}
                                index={i}
                            >
                                {item.label}
                            </Card>
                        )
                    );
                })}
            </StaggerGrid>
        );
    };

    const renderProducts = () => {
        const productsList = filterProducts(products);

        return (
            <StaggerGrid key="products">
                {productsList.map((item, i) => {
                    const image = item.image;
                    const onClick = e => {
                        (item?.sizes3Collection.items || [])?.length > 1
                            ? handleProductClick(e, item)
                            : setOnlySize(item);
                    };
                    return (
                        <Card
                            className={styles.card}
                            key={`product-${i}`}
                            image={image}
                            index={i}
                            onClick={onClick}
                            tabIndex={tabIndex}
                        >
                            {item.displayName || item.title}
                        </Card>
                    );
                })}
            </StaggerGrid>
        );
    };

    const renderSearch = () => {
        const filteredResults = hits?.filter(
            item => item?.__typename === 'Product'
        );

        if (instantSearchInstance?.status === 'stalled') {
            return (
                <div className={styles.isSearchStalled}>
                    <span>Searching...</span>
                </div>
            );
        }

        if (filteredResults.length === 0) {
            return (
                <div className={styles.noResults}>
                    <span>No Results</span>
                </div>
            );
        }

        const search = filteredResults?.filter(
            item => item?.__typename === 'Product'
        );

        return (
            <div
                role="radiogroup"
                aria-label="Products"
                className={cx(styles.products, styles.grid)}
            >
                {search?.map((item, i) => {
                    const image = item.image;
                    return (
                        <Card
                            className={styles.card}
                            key={`product-${item.objectID}`}
                            image={image}
                            index={i}
                            onClick={
                                item?.sizes3?.length > 1
                                    ? e => handleProductClick(e, item)
                                    : () => setOnlySize(item)
                            }
                            tabIndex={tabIndex}
                        >
                            {item.displayName || item.title}
                        </Card>
                    );
                })}
            </div>
        );
    };

    const renderSizes3 = () => {
        return (
            <StaggerGrid>
                {activeProduct?.sizes3Collection?.items?.map((item, i) => {
                    const image = activeProduct?.image;
                    return (
                        <Card
                            className={styles.card}
                            key={`sizes-${i}`}
                            image={item?.image || image}
                            index={i}
                            onClick={() =>
                                setProductSize({
                                    product: activeProduct,
                                    size: item,
                                    sku: item.sku2,
                                    packagingCodeExample:
                                        item.packagingCodeExample,
                                })
                            }
                            tabIndex={tabIndex}
                        >
                            {`${activeProduct?.pageTitle} ${item.size}`}
                        </Card>
                    );
                })}
            </StaggerGrid>
        );
    };

    return (
        <div className={styles.root} ref={rootRef}>
            <div
                className={cx(styles.grid, {
                    [styles.showSearch]: showSearch,
                })}
            >
                <Breadcrumbs
                    step={productStep}
                    label={renderStepLabel()}
                    onClick={() => {
                        clear();

                        if (productStep == 1) {
                            setFinderState({
                                ...finderState,
                                category: null,
                            });
                        }
                        if (productStep == 2 && !activeProduct) {
                            setFinderState({
                                ...finderState,
                                filter: null,
                            });
                        }
                        if (activeProduct) {
                            setActiveProduct(null);
                        }
                    }}
                    isHidden={productStep === 0 && query.length === 0}
                    tabIndex={tabIndex}
                    className={styles.breadcrumbs}
                />
                <Configure
                    hitsPerPage={80}
                    filters={'(sys.contentType.sys.id:product)'}
                />
                <Search
                    query={query}
                    refine={refine}
                    clear={clear}
                    className={styles.search}
                    isActive={!activeProduct && showSearch}
                    setIsActive={setShowSearch}
                    tabIndex={tabIndex}
                />
                <div
                    className={cx(styles.searchButton)}
                    onClick={() => setShowSearch(true)}
                >
                    <SvgIcon type="search" />
                </div>
            </div>
            <AnimatePresence>{renderView()}</AnimatePresence>
        </div>
    );
};

ProductsNav.propTypes = {
    additionalCategory: PropTypes.func,
    onUpdate: PropTypes.func,
    productCategories: PropTypes.array,
    products: PropTypes.array,
    tabIndex: PropTypes.number,
};

const ProductsNavInstantSearch = props => {
    return (
        <InstantSearch
            searchClient={searchClient}
            indexName={process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_INDEX}
        >
            <ProductsNav {...props} />
        </InstantSearch>
    );
};

export default ProductsNavInstantSearch;
